import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import { Tooltip } from 'primereact/tooltip';
import { OverlayPanel } from 'primereact/overlaypanel';
import { ProgressSpinner } from 'primereact/progressspinner';
import { FullWidthCardTemplate, MediumCardTemplate } from '../Products/ProductSummaryInsights';
import { CurrencyFormatter, useCurrencyConstants } from '../../Components/CurrencyFormatter';
import { parsePaginatedListResponse } from '../../utils/paginationUtils';
import * as api from '../../Service/api';
import {
    parseLinkedPlanCount,
    planIdOf,
    plansFromPayload,
    readLinkedPlansCache,
    uniquePlans,
    writeLinkedPlansCache,
} from './linkedPlanUtils';

const toPlain = (data) => {
    if (data == null) return data;
    if (typeof data.asMutable === "function") return data.asMutable({ deep: true });
    if (typeof data.toJS === "function") return data.toJS();
    return data;
};

const toArray = (data) => plansFromPayload(data);

const customersFromPayload = (data) => {
    const plain = toPlain(data);
    if (Array.isArray(plain)) return plain;
    if (!plain || typeof plain !== "object") return [];
    if (Array.isArray(plain.content)) return plain.content;
    if (Array.isArray(plain.items)) return plain.items;
    if (Array.isArray(plain.data)) return plain.data;
    return [];
};

const parseCount = (data) => parseLinkedPlanCount(data);

const headerCount = (response) => {
    const headers = response?.headers;
    if (!headers) return 0;
    const raw = typeof headers.get === "function"
        ? (headers.get("x-total-count") || headers.get("X-Total-Count"))
        : (headers["x-total-count"] ?? headers["X-Total-Count"]);
    return parseCount(raw);
};

const countFromResponse = (response, fallbackList = []) => {
    if (!response?.ok) return fallbackList.length;
    return Math.max(parseCount(response.data), headerCount(response), fallbackList.length);
};

const toNumber = (value) => {
    if (value == null || value === "") return 0;
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};

const emptyRelationStats = {
    totalPayments: 0,
    collectedAmount: 0,
    externalAmount: 0,
    totalExternalPayments: 0,
    activeSubs: 0,
};

const statsFromCustomerPayloads = (summaryData, amountsData) => ({
    totalPayments: toNumber(summaryData?.collectedCount),
    collectedAmount: toNumber(amountsData?.collectedAmount ?? summaryData?.collected),
    externalAmount: toNumber(amountsData?.externalAmount),
    totalExternalPayments: toNumber(amountsData?.externalCount ?? summaryData?.externalCount),
    activeSubs: toNumber(summaryData?.activeSubs),
});

const addRelationStats = (acc, next) => ({
    totalPayments: acc.totalPayments + toNumber(next?.totalPayments),
    collectedAmount: acc.collectedAmount + toNumber(next?.collectedAmount),
    externalAmount: acc.externalAmount + toNumber(next?.externalAmount),
    totalExternalPayments: acc.totalExternalPayments + toNumber(next?.totalExternalPayments),
    activeSubs: acc.activeSubs + toNumber(next?.activeSubs),
});

const joinIds = (ids) => ids.filter((id) => id != null && id !== "").join(",");

const fetchCustomerRelationStats = async (customerId) => {
    const [summaryResponse, amountsResponse] = await Promise.all([
        api.getCustomerSummary(customerId),
        api.getBusinessCustomerSubscriptionAmounts(customerId),
    ]);
    return statsFromCustomerPayloads(
        summaryResponse.ok ? toPlain(summaryResponse.data) : null,
        amountsResponse.ok ? toPlain(amountsResponse.data) : null
    );
};

const hasCountValue = (count) => {
    const n = Number(count);
    return Number.isFinite(n) && n > 0;
};

const formatPaymentsHint = (count, t) => {
    if (!hasCountValue(count)) return null;
    const n = Number(count);
    return t(
        n === 1
            ? "business_customers.stat_payments_count_one"
            : "business_customers.stat_payments_count_other",
        { count: n }
    );
};

const relationCountWord = (count, oneKey, otherKey, t) =>
    t(count === 1 ? oneKey : otherKey, { count });

const StatCard = ({ title, value, hint, tooltip, loading, loadingLabel }) => (
    <div className="subscriber-detail-stat">
        <div className="subscriber-detail-stat__header">
            <div className="subscriber-detail-stat__title">{title}</div>
            {tooltip ? (
                <span
                    className="subscriber-detail-stat__info subscriber-detail-stat-info"
                    tabIndex={0}
                    aria-label={tooltip}
                    data-pr-tooltip={tooltip}
                >
                    <i className="pi pi-info-circle" />
                </span>
            ) : null}
        </div>
        {loading ? (
            <div className="subscriber-detail-stat__loader" aria-busy="true" aria-live="polite">
                <ProgressSpinner
                    className="subscriber-detail-stat__spinner"
                    style={{ width: "20px", height: "20px" }}
                    strokeWidth="2"
                    aria-label={loadingLabel}
                />
                <span className="subscriber-detail-stat__loading-text">{loadingLabel}</span>
            </div>
        ) : (
            <>
                {value ? <div className="subscriber-detail-stat__value">{value}</div> : null}
                {hint ? <div className="subscriber-detail-stat__hint">{hint}</div> : null}
            </>
        )}
    </div>
);

const SummaryStatsRow = ({ heading, stats, formatMoney, tooltipSubject, t, sectionId, loading }) => {
    const loadingLabel = t("common.loading");
    return (
        <div id={sectionId} className="subscriber-detail-stats-section">
            <h6 className="title-tile ml-0 mt-5">{heading}</h6>
            <div className="subscriber-detail-stats-grid">
                <StatCard
                    title={t("business_customers.total_collected")}
                    value={formatMoney(stats?.collectedAmount)}
                    hint={formatPaymentsHint(stats?.totalPayments, t)}
                    tooltip={t("business_customers.tooltip_collected_payments", {
                        customer: tooltipSubject,
                    })}
                    loading={loading}
                    loadingLabel={loadingLabel}
                />
                <StatCard
                    title={t("business_customers.total_externally_collected")}
                    value={formatMoney(stats?.externalAmount)}
                    hint={formatPaymentsHint(stats?.totalExternalPayments, t)}
                    tooltip={t("business_customers.tooltip_externally_collected", {
                        customer: tooltipSubject,
                    })}
                    loading={loading}
                    loadingLabel={loadingLabel}
                />
                <StatCard
                    title={t("business_customers.active_subs")}
                    value={hasCountValue(stats?.activeSubs) ? stats.activeSubs : ""}
                    hint={hasCountValue(stats?.activeSubs) ? t("subscriptions") : null}
                    tooltip={t("business_customers.tooltip_active_subscriptions", {
                        customer: tooltipSubject,
                    })}
                    loading={loading}
                    loadingLabel={loadingLabel}
                />
            </div>
        </div>
    );
};

const DetailCard = (props) => {
    const navigate = useNavigate();
    const manageMenuRef = useRef(null);
    const subscriptionMenuRef = useRef(null);
    const paymentsMenuRef = useRef(null);
    const { currencyFormat, currencySymbol } = useCurrencyConstants();

    const {
        state, t, totalActiveSubscriptions, defMembership, totalReceivedPayments,
        setDisplayBasic, setState, customerId, setVisibleInvite, onOpenLinkPlan,
        onOpenLinkCustomer, onUnlinkCustomer, onViewPrimary, linkedToPrimary, unlinkingCustomer,
        onOpenSegmentDialog, onOpenAddTag, onRemoveTag, relationsRefreshKey = 0,
        onRelationsChange, onSecondariesCueClick, onLinkedPlansCueClick,
        onPauseSubscription, onResumeSubscription, onCancelSubscription,
    } = props;

    const [secondaries, setSecondaries] = useState([]);
    const [secondariesCount, setSecondariesCount] = useState(0);
    const [fetchingSecondaries, setFetchingSecondaries] = useState(false);
    const [secondariesStatsLoading, setSecondariesStatsLoading] = useState(false);
    const [linkedPlans, setLinkedPlans] = useState(() => readLinkedPlansCache(customerId));
    const [linkedPlansCount, setLinkedPlansCount] = useState(() => readLinkedPlansCache(customerId).length);
    const [fetchingLinkedPlans, setFetchingLinkedPlans] = useState(!!customerId);
    const [linkedPlansStatsLoading, setLinkedPlansStatsLoading] = useState(false);
    const [secondariesStats, setSecondariesStats] = useState(null);
    const [secondariesStatsById, setSecondariesStatsById] = useState({});
    const [linkedPlansStats, setLinkedPlansStats] = useState(null);

    const canManage = defMembership?.role !== "VIEWER";
    const hasSegment = !!state.customerRecord?.segment?.id;
    const businessId = defMembership?.business?.id;
    const showSecondariesCue = !linkedToPrimary && secondariesCount > 0;
    const showLinkedPlansCue = linkedPlansCount > 0;

    const formatMoney = useCallback((amount) => {
        if (amount == null || Number.isNaN(Number(amount))) return "";
        return CurrencyFormatter({
            amount,
            currencySymbol,
            currencyFormat,
            as: "string",
        });
    }, [currencyFormat, currencySymbol]);

    useEffect(() => {
        const cached = customerId ? readLinkedPlansCache(customerId) : [];
        setLinkedPlans(cached);
        setLinkedPlansCount(cached.length);
        setFetchingLinkedPlans(!!customerId);
    }, [customerId]);

    useEffect(() => {
        if (!customerId) {
            setLinkedPlans([]);
            setLinkedPlansCount(0);
            setFetchingLinkedPlans(false);
            return undefined;
        }
        let cancelled = false;
        setFetchingLinkedPlans(true);

        const safeGet = (request) => request.catch(() => ({ ok: false, data: null, headers: null }));
        const cached = readLinkedPlansCache(customerId);
        if (cached.length) {
            setLinkedPlans((prev) => (prev.length ? uniquePlans([...prev, ...cached]) : cached));
            setLinkedPlansCount((prev) => Math.max(prev, cached.length));
        }

        Promise.all([
            safeGet(api.getBusinessCustomerLinkedPlansCount(customerId)),
            safeGet(api.getBusinessCustomerLinkedPlans(customerId)),
        ])
            .then(([countResponse, listResponse]) => {
                if (cancelled) return;
                const plans = listResponse?.ok ? toArray(listResponse.data) : [];
                const fromCount = countResponse?.ok ? countFromResponse(countResponse) : 0;
                const fromList = listResponse?.ok ? countFromResponse(listResponse, plans) : 0;
                const parsedCount = Math.max(fromCount, fromList, plans.length);
                if (parsedCount > 0 || plans.length > 0) {
                    const nextPlans = uniquePlans([...(plans.length ? plans : []), ...cached]);
                    const nextCount = Math.max(parsedCount, nextPlans.length);
                    setLinkedPlans(nextPlans.length ? nextPlans : cached);
                    setLinkedPlansCount(nextCount);
                    if (nextPlans.length) writeLinkedPlansCache(customerId, nextPlans);
                    return;
                }
                if (cached.length) {
                    setLinkedPlans(cached);
                    setLinkedPlansCount((prev) => Math.max(prev, cached.length));
                }
            })
            .catch(() => {
                if (!cancelled && cached.length) {
                    setLinkedPlans(cached);
                    setLinkedPlansCount((prev) => Math.max(prev, cached.length));
                }
            })
            .finally(() => {
                if (!cancelled) setFetchingLinkedPlans(false);
            });
        return () => {
            cancelled = true;
        };
    }, [customerId, relationsRefreshKey]);

    useEffect(() => {
        if (!customerId || linkedToPrimary) {
            setSecondariesCount(0);
            setSecondaries([]);
            return undefined;
        }
        let cancelled = false;
        api.getSecondaryBusinessCustomersCount(customerId)
            .then((response) => {
                if (cancelled) return;
                setSecondariesCount(response.ok ? parseCount(response.data) : 0);
            })
            .catch(() => {
                if (!cancelled) setSecondariesCount(0);
            });
        return () => {
            cancelled = true;
        };
    }, [customerId, linkedToPrimary, relationsRefreshKey]);

    useEffect(() => {
        if (!customerId || secondariesCount <= 0 || linkedToPrimary) {
            setSecondaries([]);
            setFetchingSecondaries(false);
            return undefined;
        }
        let cancelled = false;
        setFetchingSecondaries(true);
        api.getSecondaryBusinessCustomers(customerId)
            .then((response) => {
                if (cancelled) return;
                setSecondaries(response.ok ? customersFromPayload(response.data) : []);
            })
            .catch(() => {
                if (!cancelled) setSecondaries([]);
            })
            .finally(() => {
                if (!cancelled) setFetchingSecondaries(false);
            });
        return () => {
            cancelled = true;
        };
    }, [customerId, secondariesCount, linkedToPrimary, relationsRefreshKey]);

    useEffect(() => {
        if (!showSecondariesCue) {
            setSecondariesStats(null);
            setSecondariesStatsById({});
            setSecondariesStatsLoading(false);
            return undefined;
        }
        const ids = secondaries.map((row) => row.id).filter(Boolean);
        if (ids.length === 0) {
            if (!fetchingSecondaries) {
                setSecondariesStats({ ...emptyRelationStats });
                setSecondariesStatsById({});
                setSecondariesStatsLoading(false);
            }
            return undefined;
        }
        let cancelled = false;
        setSecondariesStatsLoading(true);
        Promise.all(ids.map(async (id) => ({
            id,
            stats: await fetchCustomerRelationStats(id),
        })))
            .then((rows) => {
                if (cancelled) return;
                const byId = {};
                rows.forEach(({ id, stats }) => {
                    byId[id] = stats;
                });
                setSecondariesStats(rows.reduce(
                    (acc, row) => addRelationStats(acc, row.stats),
                    { ...emptyRelationStats }
                ));
                setSecondariesStatsById(byId);
            })
            .catch(() => {
                if (!cancelled) {
                    setSecondariesStats({ ...emptyRelationStats });
                    setSecondariesStatsById({});
                }
            })
            .finally(() => {
                if (!cancelled) setSecondariesStatsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [showSecondariesCue, secondaries, fetchingSecondaries]);

    useEffect(() => {
        const planIds = linkedPlans.map((plan) => planIdOf(plan)).filter(Boolean);
        if (!showLinkedPlansCue) {
            setLinkedPlansStats(null);
            setLinkedPlansStatsLoading(false);
            return undefined;
        }
        if (!businessId || planIds.length === 0) {
            if (!fetchingLinkedPlans && planIds.length === 0) {
                setLinkedPlansStats({ ...emptyRelationStats });
                setLinkedPlansStatsLoading(false);
            }
            return undefined;
        }
        let cancelled = false;
        const planIdIn = joinIds(planIds);
        setLinkedPlansStatsLoading(true);

        Promise.all([
            api.getCountSubscriptions({
                "planId.in": planIdIn,
                "status.equals": "ACTIVE",
            }),
            api.getPaymentTrackCount({ "planId.in": planIdIn }),
            Promise.all(
                planIds.map((planId) => api.getFilteredDataTotals(businessId, `?planId=${planId}`))
            ),
            Promise.all(
                planIds.map((planId) => api.getExternalPaymentTotals(businessId, `?planId=${planId}`))
            ),
            Promise.all(
                planIds.map((planId) =>
                    api.getExternalPaymentTrack(businessId, `?planId=${planId}`, {
                        page: 0,
                        size: 1,
                    })
                )
            ),
        ])
            .then(([
                activeResponse,
                paymentsResponse,
                collectedResponses,
                externalResponses,
                externalCountResponses,
            ]) => {
                if (cancelled) return;
                setLinkedPlansStats({
                    totalPayments: paymentsResponse.ok ? parseCount(paymentsResponse.data) : 0,
                    collectedAmount: collectedResponses.reduce(
                        (sum, response) =>
                            sum + (response.ok ? toNumber(toPlain(response.data)?.collected) : 0),
                        0
                    ),
                    externalAmount: externalResponses.reduce(
                        (sum, response) =>
                            sum + (response.ok ? toNumber(toPlain(response.data)?.external) : 0),
                        0
                    ),
                    totalExternalPayments: externalCountResponses.reduce((sum, response) => {
                        if (!response.ok) return sum;
                        const parsed = parsePaginatedListResponse(response, { size: 1 });
                        return sum + toNumber(parsed.totalElements);
                    }, 0),
                    activeSubs: activeResponse.ok ? parseCount(activeResponse.data) : 0,
                });
            })
            .catch(() => {
                if (!cancelled) setLinkedPlansStats({ ...emptyRelationStats });
            })
            .finally(() => {
                if (!cancelled) setLinkedPlansStatsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [showLinkedPlansCue, linkedPlans, businessId, fetchingLinkedPlans]);

    useEffect(() => {
        onRelationsChange?.({
            secondaries,
            secondariesCount,
            fetchingSecondaries,
            linkedPlans,
            linkedPlansCount,
            fetchingLinkedPlans,
            showSecondariesCue,
            showLinkedPlansCue,
            secondariesStatsById,
            secondariesStatsLoading,
        });
    }, [
        onRelationsChange,
        secondaries,
        secondariesCount,
        fetchingSecondaries,
        linkedPlans,
        linkedPlansCount,
        fetchingLinkedPlans,
        showSecondariesCue,
        showLinkedPlansCue,
        secondariesStatsById,
        secondariesStatsLoading,
    ]);

    const subscriptionStats = useMemo(() => {
        const summary = toPlain(state.customerSummary);
        const amounts = toPlain(state.businessCustomerSubscriptionAmounts);
        const subscriptions = Array.isArray(state.businessCustomerSubscriptions)
            ? state.businessCustomerSubscriptions
            : [];
        const activeFromList = subscriptions.filter(
            (row) => String(row?.status).toUpperCase() === "ACTIVE"
        ).length;
        const paymentsFromList = subscriptions.reduce(
            (sum, row) => sum + (row?.subscriber?.successCount || 0),
            0
        );

        return {
            totalPayments: summary?.collectedCount ?? totalReceivedPayments ?? paymentsFromList,
            collectedAmount: amounts?.collectedAmount ?? summary?.collected,
            externalAmount: amounts?.externalAmount,
            totalExternalPayments: amounts?.externalCount ?? summary?.externalCount,
            activeSubs: summary?.activeSubs ?? totalActiveSubscriptions ?? activeFromList,
        };
    }, [
        state.customerSummary,
        state.businessCustomerSubscriptionAmounts,
        state.businessCustomerSubscriptions,
        totalReceivedPayments,
        totalActiveSubscriptions,
    ]);

    const subscriptionRows = Array.isArray(state.businessCustomerSubscriptions)
        ? state.businessCustomerSubscriptions
        : [];
    const canPauseSubscription = subscriptionRows.some((row) => row?.status === "ACTIVE");
    const canResumeSubscription = subscriptionRows.some((row) => row?.status === "PAUSED");
    const canCancelSubscription = subscriptionRows.some((row) => row?.status === "ACTIVE" || row?.status === "PAUSED");

    const quickActions = [
        {
            label: canManage ? t("business_customers.edit_customer") : null,
            icon: "pi-pencil",
            onclick: () => {
                setDisplayBasic(true)
                setState((current) => ({
                    ...current,
                    data: current,
                    id: customerId,
                    name: current.customerRecord?.name,
                    customerNum: current.customerRecord?.customerNum,
                }))
            }
        },
        {
            label: t("business_customers.add"),
            icon: "pi-plus-circle",
            onclick: () => setVisibleInvite(true),
        },
        {
            label: t("business_customers.collect_pay_single"),
            icon: "pi-dollar",
            onclick: () => setState((current) => ({ ...current, showCollectPopup: true, oneOffOnly: true })),
        },
        {
            label: t("business_customers.record_pay_single"),
            icon: "pi-book",
            onclick: () => setState((current) => ({ ...current, showRecordPopup: true, oneOffOnly: true })),
        },
    ];

    const manageCustomerActions = [
        {
            label: hasSegment ? t("segment.switch_seg") : t("segment.add_segment"),
            icon: hasSegment ? "pi-sync" : "pi-sitemap",
            onclick: () => onOpenSegmentDialog?.(),
        },
        {
            label: t("tags.add_label"),
            icon: "pi-tag",
            onclick: () => onOpenAddTag?.(),
        },
        {
            label: canManage ? t("business_customers.link_plan") : null,
            icon: "pi-link",
            onclick: () => onOpenLinkPlan(),
        },
        ...(canManage && linkedToPrimary ? [{
            label: t("business_customers.view_primary"),
            icon: "pi-user",
            onclick: () => onViewPrimary(),
        }] : []),
        {
            label: canManage
                ? (linkedToPrimary
                    ? (unlinkingCustomer ? t("business_customers.unlinking") : t("business_customers.menu_unlink_primary"))
                    : t("business_customers.menu_link_primary"))
                : null,
            icon: linkedToPrimary ? "pi-times" : "pi-users",
            disabled: linkedToPrimary && unlinkingCustomer,
            onclick: () => {
                if (linkedToPrimary) {
                    if (!unlinkingCustomer) onUnlinkCustomer();
                    return;
                }
                onOpenLinkCustomer();
            },
        },
    ];

    const subscriptionActions = canManage ? [
        {
            label: t("subscriber.menu_pause"),
            icon: "pi-pause",
            disabled: !canPauseSubscription,
            onclick: () => onPauseSubscription?.(),
        },
        {
            label: t("subscriber.menu_resume"),
            icon: "pi-refresh",
            disabled: !canResumeSubscription,
            onclick: () => onResumeSubscription?.(),
        },
        {
            label: t("subscriber.menu_cancel"),
            icon: "pi-stop-circle",
            disabled: !canCancelSubscription,
            onclick: () => onCancelSubscription?.(),
        },
    ] : [];

    const paymentActions = canManage ? [
        { label: t("payments.collected"), icon: "pi-check-circle", onclick: () => redirectToPaymentsPage("collected", state.customerRecord?.id) },
        { label: t("payments.external"), icon: "pi-book", onclick: () => redirectToPaymentsPage("external", state.customerRecord?.id) },
        { label: t("payments.missed"), icon: "pi-exclamation-circle", onclick: () => redirectToPaymentsPage("missed", state.customerRecord?.id) },
        { label: t("payments.collected_and_external"), icon: "pi-wallet", onclick: () => redirectToPaymentsPage("accounts", state.customerRecord?.id) },
    ] : [];

    const renderMenuItems = (items) => items.filter(({ label }) => label).map(({ label, icon, onclick, disabled }) => (
        <div className="col-12" key={label}>
            <span
                onClick={disabled ? undefined : () => onclick?.()}
                className={disabled ? "subscriber-detail-action--disabled" : undefined}
            >
                <i className={"pi " + icon} />
                <span>{label}</span>
            </span>
            <hr />
        </div>
    ));

    const renderCollapsedSection = (title, icon, items, menuRef) => {
        const visible = items.filter(({ label }) => label);
        if (!visible.length) return null;
        const openBesideArrow = (event) => {
            event.preventDefault();
            event.stopPropagation();
            [manageMenuRef, subscriptionMenuRef, paymentsMenuRef].forEach((ref) => {
                if (ref !== menuRef) ref.current?.hide();
            });
            menuRef.current?.toggle(event);
        };
        return (
            <div className="col-12" key={title}>
                <span
                    className="subscriber-detail-collapsed-action"
                    onClick={openBesideArrow}
                    aria-haspopup="true"
                >
                    <i className={"pi " + icon} />
                    <span>{title}</span>
                    <i className="pi pi-angle-right subscriber-detail-payments-trigger" />
                </span>
                <OverlayPanel
                    ref={menuRef}
                    className="custom-overlay subscriber-statement-menu subscriber-detail-submenu"
                    style={{ backgroundColor: "#fff" }}
                >
                    <ul className="user-profile-items subscriber-detail-submenu__list">
                        {visible.map(({ label, icon: itemIcon, onclick, disabled }) => (
                            <li key={label}>
                                <div className="p-link list-button">
                                    <span
                                        className={`list-items subscriber-statement-menu__item${disabled ? " subscriber-detail-action--disabled" : ""}`}
                                        onClick={() => {
                                            if (disabled) return;
                                            menuRef.current?.hide();
                                            onclick?.();
                                        }}
                                    >
                                        {itemIcon ? <i className={"pi " + itemIcon} aria-hidden="true" /> : null}
                                        {label}
                                    </span>
                                </div>
                            </li>
                        ))}
                    </ul>
                </OverlayPanel>
                <hr />
            </div>
        );
    };

    const redirectToPaymentsPage = (type, id) => {
        const params = new URLSearchParams();
        params.set('type', type);
        params.set('bcId', id);

        navigate({
            pathname: '/payments',
            search: params.toString(),
        });
    };

    const subscriberMainCardDetails = [
        {
            page: "subscriber-detail",
            name: state.customerRecord?.name,
            phoneNo: state.customerRecord?.phoneNo,
            email: state.customerRecord?.email,
            linkStatus: linkedToPrimary
                ? t("business_customers.secondary")
                : t("business_customers.primary"),
            linkStatusClass: linkedToPrimary
                ? "customer-link-badge customer-link-badge--secondary"
                : "customer-link-badge customer-link-badge--primary",

            refTitle: t("business_customers.customer_ref"),
            reference: state.customerRecord?.reference,
            cusNumTitle: t("business_customers.reveknew_id"),
            cusNum: state.customerRecord?.customerNum,
            segmentName: state.customerRecord?.segment?.name,
            tags: state.customerSummary?.tags,
            canManageTags: true,
            onRemoveTag: onRemoveTag,
            showSecondariesCue,
            showLinkedPlansCue,
            secondariesCount,
            linkedPlansCount,
            secondariesCueLabel: relationCountWord(
                secondariesCount,
                "business_customers.secondaries_count_one",
                "business_customers.secondaries_count_other",
                t
            ),
            linkedPlansCueLabel: relationCountWord(
                linkedPlansCount,
                "business_customers.linked_plans_count_one",
                "business_customers.linked_plans_count_other",
                t
            ),
            onSecondariesCueClick,
            onLinkedPlansCueClick,
        }
    ]

    const subscriberInsightCard = [
        {
            type: "subscriber-details-1",
            title: t("subscriber.upcoming"),
            cardInfo: t("business_customers.tooltip_upcoming_payments", { customer: state.customerRecord?.name }),
            dataFromSubscriberDetail: state.recentSubscriptionActivityObject,
            flexAmount: t("business_customers.flexAmount"),
            currency: defMembership.business.country.currency,
            dateClassName: "yellow-progress-txts"
        },
        {
            type: "subscriber-details-2",
            title: t("subscriber.recent_payments"),
            cardInfo: t("business_customers.tooltip_recent_collected_payments", { customer: state.customerRecord?.name }),
            dataFromSubscriberDetail: state.recentPaymentsObject,
            flexAmount: t("business_customers.flexAmount"),
            currency: defMembership.business.country.currency,
            dateClassName: "green-success-txts"
        },
        {
            type: "subscriber-details-3",
            title: t("subscriber.recent_missed_payments"),
            cardInfo: t("business_customers.tooltip_recent_missed_payments", { customer: state.customerRecord?.name }),
            flexAmount: t("business_customers.flexAmount"),
            currency: defMembership.business.country.currency,
            dataFromSubscriberDetail: state.recentMissedPaymentsObject,
            dateClassName: "red-cancelled-txts"
        }
    ]

    const subscriberName = state.customerRecord?.name;
    const secondariesLoading = showSecondariesCue && (
        fetchingSecondaries || secondariesStatsLoading || secondariesStats == null
    );
    const linkedPlansLoading = showLinkedPlansCue && (
        fetchingLinkedPlans || linkedPlansStatsLoading || linkedPlansStats == null
    );

    return (
        <div className="subscriber-detail-cards">
            <Tooltip target=".subscriber-detail-stat-info" />
            <div className='grid nested-grid subscriber-detail-layout'>
                <section className='col-12 xl:col-9 subscriber-detail-main'>
                    <div className='grid mb-3 subscriber-detail-summary-grid'>
                        <FullWidthCardTemplate summary={subscriberMainCardDetails} />
                    </div>

                    <SummaryStatsRow
                        sectionId="subscriber-detail-stats-subscriptions"
                        heading={t("business_customers.subscriptions_and_payments")}
                        stats={subscriptionStats}
                        formatMoney={formatMoney}
                        tooltipSubject={subscriberName}
                        t={t}
                    />
                    {showSecondariesCue ? (
                        <SummaryStatsRow
                            sectionId="subscriber-detail-stats-secondaries"
                            heading={t("business_customers.secondaries_summary")}
                            stats={secondariesStats}
                            formatMoney={formatMoney}
                            tooltipSubject={t("business_customers.secondaries")}
                            t={t}
                            loading={secondariesLoading}
                        />
                    ) : null}
                    {showLinkedPlansCue ? (
                        <SummaryStatsRow
                            sectionId="subscriber-detail-stats-linked-plans"
                            heading={t("business_customers.linked_plans_summary")}
                            stats={linkedPlansStats}
                            formatMoney={formatMoney}
                            tooltipSubject={t("business_customers.linked_plans")}
                            t={t}
                            loading={linkedPlansLoading}
                        />
                    ) : null}

                    <div className='grid subscriber-detail-insights-grid mt-3'>
                        <MediumCardTemplate summary={subscriberInsightCard} />
                    </div>
                </section>

                <section className='col-12 xl:col-3 subscriber-detail-sidebar'>
                    <div id="side-menu" className="subscriber-detail-side-menu">
                        <p className='side-title'>{t("subscriber.quick")}</p>
                        {renderMenuItems(quickActions)}

                        <p className='side-title'>{t("subscriber.other_actions")}</p>
                        {renderCollapsedSection(t("subscriber.manage_customer"), "pi-user", manageCustomerActions, manageMenuRef)}
                        {renderCollapsedSection(t("subscriber.title"), "pi-list", subscriptionActions, subscriptionMenuRef)}
                        {renderCollapsedSection(t("subscriber.view_payments"), "pi-credit-card", paymentActions, paymentsMenuRef)}
                    </div>
                </section>

            </div>

        </div>
    )
}
export default DetailCard;
